import SectionFonctionnement from '../../classes/notePresentation/SectionFonctionnement';
import CodeBlockNotePresentation from '../../constants/simulation/codeBlockNotePresentation';
import type LigneNotePresentation from '../../interfaces/simulation/ligneNotePresentation';
import type NotePresentation from '../../interfaces/simulation/notePresentation';
import type Simulation from '../../interfaces/simulation/simulation';
import { getTitreLigne, ordonnerLignes } from './notePresentation';

interface BlocNote {
	getContenu(): string;
	getTitre(): string;
}

type NiveauTitre = 'h1' | 'h2' | 'h3';

const NIVEAUX_CHAMP_LIBRE: Partial<
	Record<CodeBlockNotePresentation, NiveauTitre>
> = {
	[CodeBlockNotePresentation.TITRE]: 'h1',
	[CodeBlockNotePresentation.SOUS_TITRE]: 'h2',
	[CodeBlockNotePresentation.PARAGRAPHE]: 'h3',
};

// Blocs dont le contenu est calculé à partir de la simulation, les autres
// (graphiques, tableaux, ratios) ne sont pas encore rédigés
const BLOCS_GENERES: Partial<
	Record<CodeBlockNotePresentation, new (simulation: Simulation) => BlocNote>
> = {
	[CodeBlockNotePresentation.SECTION_FONCTIONNEMENT]: SectionFonctionnement,
};

const NIVEAU_BLOC_GENERE: NiveauTitre = 'h3';

const CARACTERES_ECHAPPES: Record<string, string> = {
	'&': '&amp;',
	'<': '&lt;',
	'>': '&gt;',
	'"': '&quot;',
	"'": '&#39;',
};

// Le document est affiché dans une iframe : la feuille A4 est dessinée à
// l'écran puis remise à plat à l'impression, où les marges viennent de @page
const STYLE = `@page { margin: 2cm; size: A4; }
body { background-color: #f3f4f5; color: #000; font-family: Arial, Helvetica, sans-serif; font-size: 11pt; line-height: 1.5; margin: 0; }
.page { background-color: #fff; border: 1px solid #d4d4d5; box-shadow: 0 2px 8px rgb(0 0 0 / 15%); box-sizing: border-box; margin: 1cm auto; min-height: 29.7cm; padding: 2cm; width: 21cm; }
h1 { font-size: 20pt; margin: 0 0 0.5rem; text-align: center; }
h2 { font-size: 15pt; font-weight: normal; margin: 0 0 2rem; text-align: center; }
h3 { font-size: 13pt; margin: 1.5rem 0 0.5rem; }
h1, h2, h3 { break-after: avoid; }
p { margin: 0 0 0.5rem; text-align: justify; }
@media print {
	body { background-color: transparent; }
	.page { border: none; box-shadow: none; margin: 0; min-height: 0; padding: 0; width: auto; }
}`;

const echapper = (texte: string): string =>
	texte.replace(/[&<>"']/g, (caractere) => CARACTERES_ECHAPPES[caractere]);

// Les champs de la note sont nullables en base : ils ne sont pas garantis
// définis pour autant qu'ils soient déclarés optionnels
const rendreParagraphes = (contenu?: string): string =>
	(contenu ?? '')
		.split(/\n{2,}/)
		.map((paragraphe) => paragraphe.trim())
		.filter(Boolean)
		.map(
			(paragraphe) => `<p>${echapper(paragraphe).replace(/\n/g, '<br />')}</p>`,
		)
		.join('');

const rendreTitreEtContenu = (
	niveau: NiveauTitre,
	titre?: string,
	contenu?: string,
): string =>
	`${titre ? `<${niveau}>${echapper(titre)}</${niveau}>` : ''}${rendreParagraphes(contenu)}`;

const rendreBloc = (
	ligne: LigneNotePresentation,
	simulation: Simulation,
): string => {
	const codeBlock = ligne?.codeBlock as CodeBlockNotePresentation;
	const niveau = NIVEAUX_CHAMP_LIBRE[codeBlock];

	if (niveau) {
		return rendreTitreEtContenu(
			niveau,
			getTitreLigne(ligne),
			ligne.contenuLong,
		);
	}

	const BlocGenere = BLOCS_GENERES[codeBlock];

	if (!BlocGenere) {
		return '';
	}

	const bloc = new BlocGenere(simulation);
	const contenu = bloc.getContenu();

	// Un bloc généré hors de son type de budget n'a rien à imprimer
	return contenu
		? rendreTitreEtContenu(NIVEAU_BLOC_GENERE, bloc.getTitre(), contenu)
		: '';
};

const rendreCorps = (note: NotePresentation, simulation: Simulation): string =>
	ordonnerLignes(note?.lignes)
		.map((ligne) => rendreBloc(ligne, simulation))
		.join('');

const rendreDocument = (libelle: string, corps: string): string =>
	`<!doctype html><html lang="fr"><head><meta charset="utf-8" /><title>${echapper(libelle)}</title><style>${STYLE}</style></head><body><div class="page">${corps}</div></body></html>`;

// Renvoie une chaîne vide quand la trame n'a aucun bloc imprimable : il n'y a
// alors pas d'aperçu à ouvrir
const genererDocumentNotePresentation = (
	note: NotePresentation,
	simulation: Simulation,
): string => {
	const corps = rendreCorps(note, simulation);

	return corps ? rendreDocument(note?.libelle ?? '', corps) : '';
};

export default genererDocumentNotePresentation;
